import { createContext, ReactNode, useContext } from 'react'

import { Spinner } from '@iws/admin-ui/spinner'

import { useSession } from '@/contexts/SessionContext'
import { useRegister } from '@/hooks/features/register'
import { PosModel } from '@/types'

const RegisterContext = createContext<PosModel.Register | null>(null)

/**
 * The open register (counter, cashier, tax rate). Nothing renders until it has loaded; before sign-in and device
 * setup there is no register, and the sign-in screens render without one.
 */
export const RegisterProvider = ({ children }: { children: ReactNode }) => {
  const { stage } = useSession()
  const ready = stage === 'demo' || stage === 'ready'
  const { data, error } = useRegister(ready)
  if (!ready) return <>{children}</>
  if (error) return <div className="grid h-dvh place-items-center text-sm text-error">{error.message}</div>
  if (!data)
    return (
      <div className="grid h-dvh place-items-center bg-page">
        <Spinner />
      </div>
    )
  return <RegisterContext.Provider value={data}>{children}</RegisterContext.Provider>
}

/** For providers that also wrap the sign-in screens, where there is no register yet. */
export const useOptionalRegisterContext = () => useContext(RegisterContext)

export const useRegisterContext = () => {
  const ctx = useContext(RegisterContext)
  if (!ctx) throw new Error('useRegisterContext must be used inside RegisterProvider')
  return ctx
}
